import { UploadOutlined } from "@ant-design/icons"
import { Button, Upload, message } from "antd"
import React, { useState } from "react"
import { collection, writeBatch, doc, getDocs } from "firebase/firestore"
import { db } from "../../../firebase/config"
import * as XLSX from "xlsx"

export default function ImportRollsFile() {
  const [loading, setLoading] = useState(false)

  const handleFileUpload = (file) => {
    setLoading(true)

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const materialsCollection = collection(db, "materials")
        const materialsSnapshot = await getDocs(materialsCollection)
        const materialsMap = new Map()
        materialsSnapshot.forEach((materialDoc) => {
          const materialData = materialDoc.data()
          const eanAsNumber = Number(materialData.EAN)
          if (!isNaN(eanAsNumber)) {
            materialsMap.set(eanAsNumber, {
              id: materialDoc.id,
              ...materialData,
            })
          }
        })

        if (materialsMap.size === 0) {
          message.warn("No materials with a numeric EAN found. Materials will not be linked.")
        }

        const data = e.target.result
        const workbook = XLSX.read(data, { type: "array" })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

        if (rows.length < 2) {
          message.error("The Excel file is empty or has no data.")
          setLoading(false)
          return
        }

        let headerRowIndex = -1
        let headerRow
        for (let i = 0; i < rows.length; i++) {
          if (rows[i] && rows[i].includes("Roll ID")) {
            headerRowIndex = i
            headerRow = rows[i]
            break
          }
        }

        if (headerRowIndex === -1) {
          message.error("Could not find the header row. Make sure a 'Roll ID' column exists.")
          setLoading(false)
          return
        }

        const rollIdIndex = headerRow.indexOf("Roll ID")
        const lengthIndex = headerRow.indexOf("Length")
        const widthIndex = headerRow.indexOf("Width()")
        const weightIndex = headerRow.indexOf("Net Weight")
        const coreIndex = headerRow.indexOf("Core")
        const materialEanIndex = headerRow.indexOf("Material")

        if (materialEanIndex === -1) {
          message.error(
            "Could not find the 'Material' column in the Excel file. Materials cannot be linked."
          )
          setLoading(false)
          return
        }

        const dataRows = rows.slice(headerRowIndex + 1)
        const batch = writeBatch(db)
        const rollStoreCollection = collection(db, "rollStore")
        let linkedCount = 0

        const unmatchedRolls = []

        dataRows.forEach((row) => {
          if (!row || row.length === 0) return

          const eanValueFromExcel = Number(row[materialEanIndex])
          const matchedMaterial = !isNaN(eanValueFromExcel)
            ? materialsMap.get(eanValueFromExcel)
            : undefined

          let materialToSave = {}
          let materialIdToSave = ""
          let eurM2 = 0 // MODIFICATION: Initialize eurM2

          if (matchedMaterial) {
            const { rolls, ...restOfMaterial } = matchedMaterial
            materialToSave = restOfMaterial
            materialIdToSave = matchedMaterial.id
            eurM2 = Number(restOfMaterial.eurM2) || 0 // MODIFICATION: Get eurM2 from the material
            linkedCount++
          } else {
            if (row[rollIdIndex]) {
              unmatchedRolls.push({
                "Roll ID": row[rollIdIndex],
                "EAN Searched For": row[materialEanIndex],
              })
            }
          }

          const lengthStr = String(row[lengthIndex] || "0")
          const widthStr = String(row[widthIndex] || "0")
          const weightStr = String(row[weightIndex] || "0")

          const originalMeters = Number(lengthStr.replace(",", ".")) || 0
          const rollWidthInMM = (Number(widthStr.replace(",", ".")) || 0) * 10

          // --- MODIFICATION: Calculate and round the price ---
          let calculatedPrice = 0
          if (eurM2 > 0 && originalMeters > 0 && rollWidthInMM > 0) {
            const rollWidthInM = rollWidthInMM / 1000 // Convert width from mm to meters
            const rawPrice = originalMeters * rollWidthInM * eurM2
            calculatedPrice = Number(rawPrice.toFixed(2)) // Round to 2 decimal places
          }

          const newRollData = {
            barcode: row[rollIdIndex] || "",
            remainingMeters: originalMeters,
            originalMeters: originalMeters,
            rollWidth: rollWidthInMM,
            weight: Number(weightStr.replace(",", ".")) || 0,
            core: String(row[coreIndex] || ""),
            material: materialToSave,
            materialId: materialIdToSave,
            orderNumber: 0,
            shipping: false,
            fullOrderNumber: "",
            rollLocation: 0,
            inOrder: false,
            comment: `Imported on ${new Date().toLocaleDateString()}`,
            price: calculatedPrice, // MODIFICATION: Use the calculated price
            palletLocation: "",
            pallet: "",
            status: "shipping",
            orderId: "",
            updated: new Date(),
            inUse: false,
            created: new Date(),
            verified: false,
          }

          if (newRollData.barcode) {
            const newDocRef = doc(rollStoreCollection)
            batch.set(newDocRef, newRollData)
          }
        })

        await batch.commit()

        message.success(`Import complete! ${linkedCount} rolls were linked to a material.`)

        if (unmatchedRolls.length > 0) {
          console.warn(`Could not find a matching material for ${unmatchedRolls.length} roll(s):`)
          console.table(unmatchedRolls)
        }
      } catch (error) {
        console.error("Error processing Excel file:", error)
        message.error("Failed to process the Excel file. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    reader.readAsArrayBuffer(file)

    return false
  }

  return (
    <Upload name="file" beforeUpload={handleFileUpload} showUploadList={false} accept=".xlsx, .xls">
      <Button icon={<UploadOutlined />} loading={loading}>
        {loading ? "Importing..." : "Import File"}
      </Button>
    </Upload>
  )
}
